import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight, Radar } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ImportanceBadge from "../components/intel/ImportanceBadge";
import { SkeletonCards, ErrorState, EmptyState } from "../components/intel/IntelStates";
import { intelApi, fmtDate } from "../lib/intelApi";
import { useLenis } from "../hooks/useLenis";

const weekNum = (w) => parseInt(w.split("-W")[1], 10);

export default function BriefArchivePage({ theme, toggleTheme }) {
  useLenis();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["intel-briefs"], queryFn: intelApi.briefs });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Weekly Briefs Archive — Madan Aritakula";
    return () => {
      document.title = "Madan Aritakula — Cloud Security Engineer | AWS · Azure · GCP · OCI | IAM, SIEM pipelines, CSPM";
    };
  }, []);

  const weeks = data?.weeks || [];
  const totals = weeks.reduce((a, w) => ({ updates: a.updates + w.total, high: a.high + w.high }), { updates: 0, high: 0 });

  return (
    <div className="grain relative min-h-screen bg-background text-foreground selection:bg-accent selection:text-accent-foreground">
      <Nav theme={theme} toggleTheme={toggleTheme} />
      <main data-testid="briefs-archive-page" className="pt-28 md:pt-40 pb-24 md:pb-40">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 space-y-12 md:space-y-16">
          <header className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-3">
              <div className="font-mono text-xs md:text-sm text-accent tracking-[0.2em] uppercase">Archive</div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Weekly briefs</div>
              {data && (
                <div className="mt-6 space-y-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="archive-totals">
                  <div>{weeks.length} weeks tracked</div>
                  <div>{totals.updates} updates · <span className="text-accent">{totals.high} high/critical</span></div>
                </div>
              )}
            </motion.div>
            <div className="md:col-span-9">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] uppercase"
                data-testid="archive-title"
              >
                Every week,
                <br />
                <span className="text-accent">on record</span>.
              </motion.h1>
              <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-[56ch]">
                A running log of what changed across the cloud platforms I secure — one AI-ranked brief per ISO week, from the
                first ingested update to today.
              </p>
            </div>
          </header>

          {isLoading && <SkeletonCards count={4} />}
          {isError && <ErrorState onRetry={refetch} />}
          {data && weeks.length === 0 && <EmptyState title="No briefs yet" body="Weeks appear here once updates have been ingested." />}

          {weeks.length > 0 && (
            <ol className="border-t border-border" data-testid="archive-list">
              {weeks.map((w, i) => (
                <motion.li
                  key={w.week}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.04 }}
                >
                  <Link
                    to={`/brief/${w.week}`}
                    data-testid={`archive-week-${w.week}`}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[150px_1fr_auto] gap-x-6 md:gap-x-10 gap-y-3 py-6 md:py-7 border-b border-border hover:bg-surface/60 transition-colors -mx-4 px-4"
                  >
                    <div>
                      <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter leading-none tabular-nums">
                        W{String(weekNum(w.week)).padStart(2, "0")}
                        {w.current && <span className="ml-2 align-middle font-mono text-[9px] uppercase tracking-[0.2em] text-accent">live</span>}
                      </div>
                      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {fmtDate(w.from)} → {fmtDate(new Date(new Date(w.to).getTime() - 86400000).toISOString())}
                      </div>
                    </div>
                    <div className="min-w-0 col-span-2 md:col-span-1">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        <span className="text-foreground">{w.total} updates</span>
                        <span className={w.critical ? "text-destructive" : ""}>{w.critical} critical</span>
                        <span className="text-accent">{w.high} high+</span>
                        <span>{w.security} security</span>
                        <span>{w.actions} actions</span>
                      </div>
                      {w.headline && (
                        <div className="mt-3 flex flex-wrap items-start gap-2">
                          <ImportanceBadge level={w.headline.importance} />
                          <span className="font-heading font-bold text-sm md:text-base tracking-tight leading-snug group-hover:text-accent transition-colors">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mr-2">{w.headline.technology}</span>
                            {w.headline.title}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="hidden md:inline-flex self-center text-accent opacity-60 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight size={18} />
                    </span>
                  </Link>
                </motion.li>
              ))}
            </ol>
          )}

          <div className="border border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground max-w-[56ch] inline-flex items-start gap-2">
              <Radar size={14} className="text-accent mt-0.5 shrink-0" /> Feeds are ingested every 6 hours; each week's brief is computed live from the stored updates.
            </p>
            <Link to="/intelligence" data-testid="archive-open-dashboard" className="shrink-0 inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">
              Open dashboard <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
